import React from 'react';

import { CATEGORY_REVENUE, DIRECTION_RETURNED, fundingSources, money } from './changeRequestFormat';

const SEGMENTS = ['local', 'central', 'profit'];

const SEGMENT_LABELS = {
    local: () => $t('Local contingency'),
    central: () => $t('Central contingency'),
    profit: () => $t('Event profit'),
};

/**
 * The calculated split as a proportional bar plus a legend. It renders the split
 * the server sent and computes nothing of its own — which is what makes the table
 * and this pop-up structurally incapable of disagreeing.
 *
 * Event profit is listed only when the budget has it enabled or the change moves
 * some: a legend entry for a funding source the event does not offer reads as an
 * option. A revenue change draws on no contingency, so its legend lists Event profit
 * alone, or nothing.
 */
export default function SourceOfFundsBar({ funding, category = null, profitEnabled = false }) {
    const amounts = {
        local: Number(funding?.local ?? 0),
        central: Number(funding?.central ?? 0),
        profit: Number(funding?.profit ?? 0),
    };

    const segments = (category === CATEGORY_REVENUE ? ['profit'] : SEGMENTS).filter((key) => key !== 'profit' || profitEnabled || amounts.profit > 0);
    const total = SEGMENTS.reduce((sum, key) => sum + amounts[key], 0);
    const drawn = fundingSources(funding, category);
    const shown = Object.fromEntries(drawn.map((source) => [source.key, source]));
    const returned = funding?.direction === DIRECTION_RETURNED;

    return (
        <>
            <div className="bc-sof-bar">
                {total > 0 &&
                    SEGMENTS.filter((key) => amounts[key] > 0).map((key) => (
                        <div key={key} className={`bc-sof-bar__seg--${key}`} style={{ width: `${(amounts[key] / total) * 100}%` }} />
                    ))}
            </div>

            <div className="bc-sof-leg">
                {segments.map((key) => (
                    <span key={key}>
                        <span className={`bc-dot bc-dot--${key}`} aria-hidden="true" />
                        {legendText(key, shown[key])}
                    </span>
                ))}
            </div>

            <FundingSentence sources={drawn} returned={returned} />
        </>
    );
}

/** A segment's legend entry; a source the change does not move reads 0. */
function legendText(key, source) {
    if (source?.added) {
        return $t('{{source}} {{amount}} added', { source: SEGMENT_LABELS[key](), amount: money(source.amount) });
    }

    if (source?.returned) {
        return $t('{{source}} {{amount}} returned', { source: SEGMENT_LABELS[key](), amount: money(source.amount) });
    }

    return `${SEGMENT_LABELS[key]()} ${money(source?.amount ?? 0)}`;
}

/**
 * The line under the legend. Event profit is spelt out whenever it carries part of
 * the change, because no contingency setting explains it.
 */
function FundingSentence({ sources, returned }) {
    if (sources.length === 0) {
        return <div className="bc-sub tw-mt-1.5">{$t('This change draws on no contingency.')}</div>;
    }

    const profit = sources.find((source) => source.key === 'profit');

    if (!profit) {
        return null;
    }

    const covered = sources.filter((source) => source.key !== 'profit').reduce((sum, source) => sum + source.amount, 0);
    let sentence;

    if (profit.added) {
        sentence = $t('This change adds {{amount}} to Event profit.', { amount: money(profit.amount) });
    } else if (covered === 0) {
        sentence = returned
            ? $t('This change returns {{amount}} to Event profit.', { amount: money(profit.amount) })
            : $t('This change draws {{amount}} from Event profit.', { amount: money(profit.amount) });
    } else {
        sentence = returned
            ? $t('Contingency takes back {{covered}}; the remaining {{amount}} is returned to Event profit.', {
                  covered: money(covered),
                  amount: money(profit.amount),
              })
            : $t('Contingency covers {{covered}}; the remaining {{amount}} is drawn from Event profit.', {
                  covered: money(covered),
                  amount: money(profit.amount),
              });
    }

    return <div className="bc-sub tw-mt-1.5">{sentence}</div>;
}
